import { useEffect, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { formatDistanceToNow } from 'date-fns';
import { Link } from '@/shared/components/atoms/Link';
import { StatsPanel } from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import { OperatorLogo } from '@/shared/components/operators/Logos';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import TokenInputField from '@/shared/components/TokenInputField';
import WalletActionButton from '@/shared/components/WalletActionButton';
import useApproveScUtils from '@/shared/hooks/useApproveScUtils';
import useAuction from '@/shared/hooks/useAuction';
import useDelegateFlip from '@/shared/hooks/useDelegateFlip';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useRequiredDepositAmount from '@/shared/hooks/useRequiredDepositAmount';
import useSteps from '@/shared/hooks/useSteps';
import useToast from '@/shared/hooks/useToast';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { formatBps, formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import OperatorSelectionModal from './OperatorSelectionModal';
import { type OperatorSummary } from '../../../hooks/useOperatorsAndAuction';

export default function DelegateFlipCard({
  operator,
  openModal,
  modalOpen,
  closeModal,
  newOperator,
  selectNewOperator,
  operators,
  newDelegation = true,
}: {
  operator: OperatorSummary | undefined;
  newOperator: OperatorSummary | undefined;
  selectNewOperator: (operator: OperatorSummary) => void;
  openModal: VoidFunction;
  closeModal: VoidFunction;
  modalOpen: boolean;
  operators: OperatorSummary[];
  newDelegation?: boolean;
}) {
  const toast = useToast('delegate-flip-card');
  const [id, setId] = useState(0);
  const { step, gotoStep, advanceStep } = useSteps('approve', 'delegate');

  const { delegableBalance, evmFlipBalance, nextEpoch } = useEvmScAccount();

  const [additionalDelegation, setAdditionalDelegation] = useState<TokenAmount>();
  const requiredDepositAmount = useRequiredDepositAmount(additionalDelegation);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setError(undefined);
  }, [newOperator?.idSs58]);

  useEffect(() => {
    if (requiredDepositAmount) gotoStep('approve');
  }, [requiredDepositAmount]);

  useEffect(() => {
    setError(undefined);
  }, [additionalDelegation]);

  const { mutate: delegateFlip, isPending: isDelegating } = useDelegateFlip({
    onSuccess: () => {
      setAdditionalDelegation(undefined);
      setId((current) => current + 1);
      gotoStep('approve');
    },
  });

  const { auction } = useAuction();
  const timeTillNextEpoch = auction && ` in ${formatDistanceToNow(auction.endsAt)}`;

  const { mutate: approveScUtils, isPending: isApproving } = useApproveScUtils({
    onSuccess: advanceStep,
  });

  let buttonLabel = '';
  const someOperator = newOperator ?? operator;
  const isSwitching =
    someOperator !== undefined &&
    nextEpoch !== undefined &&
    someOperator.idSs58 !== nextEpoch.operatorIdSs58;

  if (step === 'delegate') {
    buttonLabel = `Delegate ${FLIP_SYMBOL}`;

    if (isSwitching) buttonLabel += ' & Switch Operator';
  } else if (step === 'approve') {
    buttonLabel = `Approve ${FLIP_SYMBOL}`;
  }

  const totalDelegation =
    nextEpoch?.delegatedBalance.add(additionalDelegation ?? 0) ?? TokenAmount.ZERO;

  let delegatorApy = 0;

  if (someOperator) {
    // if the user is increasing delegation to the same operator, we calculate
    // the new apy using the additional delegation from the token input
    if (!isSwitching) {
      delegatorApy = additionalDelegation?.gt(0)
        ? someOperator.nextEpoch.calculateDelegatorApy(additionalDelegation)
        : (someOperator.nextEpoch.delegatorApy.apyPct ?? 0);
    } else if (someOperator) {
      // if the user is switching operators, we calculate the apy based on their
      // total delegation
      delegatorApy =
        (totalDelegation?.gt(0)
          ? someOperator.nextEpoch.calculateDelegatorApy(totalDelegation)
          : someOperator.nextEpoch.delegatorApy.apyPct) ?? 0;
    }
  }

  return (
    <>
      <div className="flex flex-col justify-between gap-y-6 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 p-4 md:min-w-[496px]">
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-2">
            <span className="flex items-center gap-x-2 font-aeonikMedium text-14">
              Operator{' '}
              <QuestionMarkTooltip content="An Operator manages one or more Validators in the network. Delegators stake through Operators to earn rewards." />
            </span>
            <button
              type="button"
              className="flex w-full flex-col gap-y-2 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-13 text-cf-light-2 shadow-grayPop3"
              onClick={openModal}
            >
              <div className="flex w-full items-center justify-between gap-x-4">
                <div className="flex items-center gap-x-1 whitespace-nowrap">
                  {someOperator ? (
                    <>
                      <OperatorLogo size="small" idSs58={someOperator?.idSs58} />
                      <span className="text-cf-white">{someOperator.alias ?? 'Operator'}</span>
                      {abbreviate(someOperator.idSs58)}
                    </>
                  ) : (
                    'Select an Operator'
                  )}
                </div>
                <ChevronIcon orientation="left-right" />
              </div>
            </button>
          </div>
          <TokenInputField
            key={id}
            token={chainflipAssetMap.Flip}
            value={additionalDelegation}
            onChange={setAdditionalDelegation}
            balance={evmFlipBalance ?? undefined}
            accountBalance={nextEpoch?.undelegatedBalance}
            maxAmount={delegableBalance}
            error={error}
            label="Additional Delegation"
            allowZero={isSwitching}
          />
          <StatsPanel
            items={[
              {
                label: 'Est. APY',
                tooltipContent:
                  'The projected annual percentage yield based on current network conditions. Actual returns may vary.',
                content: someOperator && (
                  <span className="text-cf-green-3">{formatPct(delegatorApy)}</span>
                ),
              },
              {
                label: 'Est. Weekly Rewards',
                flipAmount: totalDelegation.mul(delegatorApy).div(365).mul(7),
                showPrice: true,
              },
              {
                label: 'Est. Yearly Rewards',
                flipAmount: totalDelegation.mul(delegatorApy),
                showPrice: true,
              },
              {
                label: 'Delegation Commission',
                tooltipContent:
                  'The percentage of rewards kept by the Operator as commission. The rest is distributed to Delegators.',
                content: someOperator && (
                  <span>{formatBps(someOperator.nextEpoch.commissionBps)}</span>
                ),
              },
            ]}
          />
        </div>
        {!newDelegation ? (
          <Callout type="info">
            Your delegated {FLIP_SYMBOL} will start earning rewards{timeTillNextEpoch} when the next
            epoch starts.{' '}
            <Link
              href="https://docs.chainflip.io/protocol/delegation"
              target="_blank"
              underline
              color="blue"
            >
              Learn more.
            </Link>
          </Callout>
        ) : null}
        <WalletActionButton
          chainId={chainflipAssetMap.Flip.chain.id}
          requireConnection
          fullWidth
          disabled="ifLoading"
          onClick={() => {
            if (!additionalDelegation || (!isSwitching && additionalDelegation?.eq(0))) {
              return setError('Please enter a valid amount');
            }

            if (!someOperator) {
              return toast.error('No operator selected');
            }

            if (step === 'delegate') {
              return delegateFlip({
                amount: additionalDelegation,
                operator: someOperator.idSs58 as `cF${string}`,
                depositAmount: requiredDepositAmount,
              });
            }

            if (step === 'approve') {
              if (!requiredDepositAmount) {
                advanceStep();
                return toast.success('On-chain balance is already sufficient');
              }
              return approveScUtils(requiredDepositAmount);
            }
          }}
          loading={(isDelegating || isApproving) && 'iconOnly'}
        >
          {buttonLabel}
        </WalletActionButton>
      </div>
      <OperatorSelectionModal
        open={modalOpen}
        onClose={closeModal}
        selectedOperator={someOperator}
        setSelectedOperator={selectNewOperator}
        delegationAmount={additionalDelegation}
        operators={operators}
      />
    </>
  );
}
